import { useTranslation } from 'react-i18next';
import { Gavel, NotepadText, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { DateTimePicker } from '@/components/common/DateTimePicker';
import { format } from 'date-fns';
import { useAuth } from '@/features/auth';
import { useMatterForm } from '../hooks';
import { MatterTeamFields } from '../components';
import { MATTER_CATEGORY_VALUES } from '../types';
import { CourtPartyFields, CourtDetailsFields } from '../components/CourtInfoFields';

export function ActionMatterPage() {
  const { t } = useTranslation();
  const { hasAccess } = useAuth();

  const {
    form,
    onSubmit,
    onInvalid,
    navigate,
    isEditMode,
    isLoadingMatter,
    isPending,
    isCourtCase,
    lockCourtCase,
    lockPostIntake,
    contractIdFromUrl,
    selectedClientId,
    setSelectedClientId,
    selectedAttorney,
    setSelectedAttorney,
    selectedStaffs,
    setSelectedStaffs,
    selectedPartner,
    setSelectedPartner,
    isMdManaged,
    setIsMdManaged,
  } = useMatterForm();

  const { isSubmitting } = form.formState;

  // Gray out the labels of post-INTAKE-locked fields too, so an empty disabled
  // input still reads as "can't be changed".
  const lockedLabelClass = lockPostIntake ? 'text-slate-400' : '';

  if (isEditMode && isLoadingMatter) {
    return (
      <div className="bg-[#F5F6F7] min-h-screen pb-24">
        <div className="h-[50px] flex items-center justify-between px-4 py-2 border-b border-[#D9D9D9]">
          <Skeleton className="h-6 w-32" />
        </div>
        <div className="w-full max-w-3xl mx-auto mt-3 px-4">
          <Skeleton className="h-[600px] w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F5F6F7] min-h-screen pb-24">
      {/* Header */}
      <div className="h-[50px] flex items-center justify-between px-4 py-2 border-b border-[#D9D9D9]">
        <p>
          {isEditMode ? `${t('update')} ${t('matter')}` : t('newMatter')}
        </p>
        <Button onClick={() => navigate(-1)} variant="ghost">
          <X />
        </Button>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((values) => onSubmit(values), onInvalid)}
          className="w-full max-w-3xl mx-auto mt-3 bg-white px-4 py-4 rounded-sm drop-shadow-sm flex flex-col gap-4"
        >
          {/* Matter Type Selection — locked once the matter is active (the
              court-case flag drives Gate 3 / court instances) */}
          <div className="flex gap-16 items-center">
            <div>
              <p className={`font-thin ${lockCourtCase ? 'text-slate-400' : ''}`}>
                {t('typeOfMatter')}
              </p>
              <div className="mt-2.5 flex gap-2.5">
                <Button
                  type="button"
                  disabled={lockCourtCase}
                  onClick={() => form.setValue('isCourtCase', true)}
                  className={`bg-[#F0F0F0] text-black hover:bg-[#F0F0F0] flex items-center gap-2 ${
                    form.watch('isCourtCase') ? 'ring-2 ring-primary' : ''
                  }`}
                >
                  <Gavel className="text-primary" /> {t('legalCase')}
                </Button>
                <Button
                  type="button"
                  disabled={lockCourtCase}
                  onClick={() => form.setValue('isCourtCase', false)}
                  className={`bg-[#F0F0F0] text-black hover:bg-[#F0F0F0] flex items-center gap-2 ${
                    !form.watch('isCourtCase') ? 'ring-2 ring-primary' : ''
                  }`}
                >
                  <NotepadText /> {t('project')}
                </Button>
              </div>
              {lockCourtCase && (
                <p className="mt-1.5 text-xs text-slate-500">
                  {t('matterTypeLocked')}
                </p>
              )}
            </div>
          </div>
          <Separator />

          {/* Name & Description */}
          <div className="flex gap-2.5">
            {isCourtCase && (
              <FormField
                control={form.control}
                name="caseNumber"
                render={({ field }) => (
                  <FormItem className="flex-1 min-w-[150px]">
                    <FormLabel>{t('matterNumber')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {lockPostIntake ? (
              /* Category is editable only while the matter is in INTAKE
                 (server-enforced too). Once active — render a plain read-only
                 field (a disabled Select can't display legacy values like
                 MIGRATION_PENDING that have no option). */
              <div className="flex-1 min-w-[150px]">
                <p className="text-sm font-medium leading-none mb-2 text-slate-400">
                  {t('matterCategory.label')}
                </p>
                <Input
                  value={
                    form.watch('category')
                      ? t(`matterCategory.${form.watch('category')}`)
                      : '—'
                  }
                  disabled
                  readOnly
                />
              </div>
            ) : (
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="required">{t('matterCategory.label')}</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value ?? undefined}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('matterCategory.select')} />
                        </SelectTrigger>
                        <SelectContent>
                          {MATTER_CATEGORY_VALUES.map((c) => (
                            <SelectItem key={c} value={c}>
                              {t(`matterCategory.${c}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>

          <FormField
            control={form.control}
            name="clientId"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel className={lockedLabelClass}>{t('selectClient')}</FormLabel>
                <FormControl>
                  <UniversalCombobox
                    url="/clients"
                    labelKeys={['lastname', 'name', 'middlename']}
                    valueKey="id"
                    selectedId={field.value}
                    disabled={lockPostIntake}
                    onSelectItem={(item) => {
                      field.onChange(item?.['id'] ?? null);
                      setSelectedClientId(item?.['id'] as number | undefined);
                    }}
                    placeholder={t('selectClient')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {!contractIdFromUrl && (
            <FormField
              control={form.control}
              name="contractId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={`required ${lockedLabelClass}`}>
                    {t('contractId')}
                  </FormLabel>
                  <FormControl>
                    <UniversalCombobox
                      url="/contracts"
                      listUrl="/contracts/select-box"
                      labelKey="name"
                      valueKey="id"
                      selectedId={field.value}
                      disabled={lockPostIntake}
                      params={selectedClientId ? { clientId: selectedClientId } : {}}
                      onSelectItem={(item) => field.onChange(item?.['id'] ?? null)}
                      placeholder={t('selectContractId')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem className="flex-1 min-w-[150px]">
                <FormLabel>{t('description')}</FormLabel>
                <FormControl>
                  <Textarea {...field} placeholder={t('enterDescription')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {isCourtCase && <CourtPartyFields control={form.control} />}
          {isCourtCase && <CourtDetailsFields control={form.control} />}

          {/* Case 360° — optional Matter Plan + initial risk. Editable here only
              while the matter is in INTAKE; afterwards the plan changes via the
              Plan tab (until Gate 2) or a Change Request, risk via Compliance. */}
          <Separator />
          <div>
            <p className={`font-thin ${lockedLabelClass}`}>
              {t('plan.cardTitle')}{' '}
              <span className="text-slate-400">({t('optional')})</span>
            </p>
            {lockPostIntake && (
              <p className="mt-1 text-xs text-slate-500">{t('plan.editFormLockedHint')}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <FormField
              control={form.control}
              name="scopeIn"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={lockedLabelClass}>{t('plan.scopeIn')}</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={2} maxLength={5000} disabled={lockPostIntake} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="scopeOut"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={lockedLabelClass}>{t('plan.scopeOut')}</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={2} maxLength={5000} disabled={lockPostIntake} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <FormField
              control={form.control}
              name="estimate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={lockedLabelClass}>
                    {t('plan.estimate')} ({t('plan.hours')})
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      min={0}
                      disabled={lockPostIntake}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="budget"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={lockedLabelClass}>{t('plan.budget')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      min={0}
                      disabled={lockPostIntake}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <FormField
              control={form.control}
              name="targetEndDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={lockedLabelClass}>{t('plan.targetEndDate')}</FormLabel>
                  <FormControl>
                    <DateTimePicker
                      mode="date"
                      value={field.value || undefined}
                      onChange={(d) => field.onChange(d ? format(d, 'yyyy-MM-dd') : '')}
                      disabled={lockPostIntake}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="riskLevel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={lockedLabelClass}>{t('riskLevel')}</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value ?? ''}
                      onValueChange={(v) => field.onChange(v || undefined)}
                      disabled={lockPostIntake}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('riskLevel')} />
                      </SelectTrigger>
                      <SelectContent>
                        {(['LOW', 'MEDIUM', 'HIGH'] as const).map((r) => (
                          <SelectItem key={r} value={r}>
                            {t(`compliance.risk.${r}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Team is assigned at creation only. On update, the matter team is
              managed from the matter detail page (reassign lead/partner,
              add/remove staff) so task ownership stays consistent. */}
          {!isEditMode && (
            <MatterTeamFields
              selectedAttorney={selectedAttorney}
              setSelectedAttorney={setSelectedAttorney}
              selectedStaffs={selectedStaffs}
              setSelectedStaffs={setSelectedStaffs}
              selectedPartner={selectedPartner}
              setSelectedPartner={setSelectedPartner}
              isMdManaged={isMdManaged}
              setIsMdManaged={setIsMdManaged}
              hasAccess={hasAccess}
            />
          )}

          <Separator />

          <div className="mt-6 flex gap-3 justify-end">
            {isEditMode ? (
              <Button type="submit" disabled={isPending || isSubmitting}>
                {isPending || isSubmitting ? t('saving') : `${t('save')} ${t('matter')}`}
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isPending || isSubmitting}
                  onClick={form.handleSubmit(
                    (values) => onSubmit(values, 'createAndContinue'),
                    onInvalid,
                  )}
                >
                  {isPending || isSubmitting ? t('saving') : t('saveAndCreateAnother')}
                </Button>
                <Button type="submit" disabled={isPending || isSubmitting}>
                  {isPending || isSubmitting ? t('saving') : `${t('save')} ${t('matter')}`}
                </Button>
              </>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
}
